import { HttpClient } from '@angular/common/http';
import { AbstractControl } from '@angular/forms';
import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { AccountPlan } from '../../model/account-plan';
import { AccountPlanDimensions } from '../../model/account-plan-dimensions';
import { CodingConfig } from '../../../invoice/model/coding';
import { DimensionLabels, defaultDimensionLabels, mergeDimensionLabels } from '../../model/dimension-labels';

@Injectable({
  providedIn: 'root',
})
export class DimensionLabelsService {
  constructor(private readonly _http: HttpClient, @Inject(APP_CONFIG) private readonly _config: IAppConfig) {}

  private readonly _cache = new Map<string, Observable<DimensionLabels>>();
  private readonly _endpoint = `${this._config.apiEndpointV2}/account-plan-dimensions`;
  private readonly _defaultLabels = this._http.get<AccountPlanDimensions>(`${this._endpoint}/default`).pipe(
    map(labels => this.fromDimensions(labels)),
    catchError(() => of(this.defaults)),
    shareReplay(1)
  );

  get defaults(): DimensionLabels {
    return defaultDimensionLabels;
  }

  getDefaultLabels(): Observable<DimensionLabels> {
    return this._defaultLabels;
  }

  getForCompany(companyId?: string | null): Observable<DimensionLabels> {
    if (!companyId) {
      return this.getDefaultLabels();
    }

    let cached = this._cache.get(companyId);
    if (!cached) {
      cached = this._http.get<AccountPlanDimensions>(`${this._endpoint}/company/${companyId}`).pipe(
        map(labels => this.fromDimensions(labels)),
        catchError(() => of(this.defaults)),
        shareReplay(1)
      );
      this._cache.set(companyId, cached);
    }

    return cached;
  }

  fromAccountPlan(accountPlan?: Partial<AccountPlan> | null): DimensionLabels {
    return mergeDimensionLabels(accountPlan);
  }

  fromCodingConfig(codingConfig?: Partial<CodingConfig> | null): DimensionLabels {
    return mergeDimensionLabels(codingConfig);
  }

  fromDimensions(dimensions?: Partial<AccountPlanDimensions> | null): DimensionLabels {
    return mergeDimensionLabels(dimensions);
  }

  setControlCaption(control: AbstractControl | null | undefined, caption: string): void {
    if (!control || !('caption' in control)) {
      return;
    }

    (control as FormControlEx & { caption: string }).caption = caption;
  }

  setControlHelpText(control: AbstractControl | null | undefined, helpText: string): void {
    if (!control || !('helpText' in control)) {
      return;
    }

    (control as FormControlEx & { helpText: string }).helpText = helpText;
  }

  setColumnCaption(columns: ColumnDefinition[] | null | undefined, binding: string, caption: string): void {
    const column = columns?.find(x => x.binding === binding);
    if (column) {
      column.caption = caption;
    }
  }
}
